/**
 * Every graded sitting in the cohort, one row per student.
 *
 * A dot per attempt rather than a bar per student, because a mean hides the two
 * things worth seeing: how many attempts it is drawn from, and how far apart
 * they were. One dot at 30% and one at 80% average to the same place as two at
 * 55%, and those are not the same student. So every attempt is plotted, the
 * count is printed next to the mean, and the mean is drawn as a tick rather
 * than as the whole story.
 *
 * The rows double as the student picker: the dashboard has no single student to
 * link to any more, so each name goes to that student's progress screen and the
 * card as a whole goes to the roster.
 */
import { Link } from "react-router-dom";
import { formatDate, formatPercent } from "../lib/format";
import type { CohortStudentRow } from "./AnalyticsData";

const WIDTH = 300;
const HEIGHT = 16;
const MID = HEIGHT / 2;
const TICKS = [0, 25, 50, 75, 100];

function StudentStrip({ row }: { row: CohortStudentRow }) {
  const x = (ratio: number) => Math.max(0, Math.min(1, ratio)) * WIDTH;

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="w-full"
      role="img"
      aria-label={`${row.student_name}: ${row.points.length} graded ${
        row.points.length === 1 ? "sitting" : "sittings"
      } at ${row.points.map((p) => formatPercent(p.ratio)).join(", ")} of the paper.`}
    >
      {TICKS.map((tick) => (
        <line
          key={tick}
          x1={x(tick / 100)}
          x2={x(tick / 100)}
          y1={0}
          y2={HEIGHT}
          stroke="var(--color-border)"
          strokeWidth={1}
        />
      ))}

      {/* The mean, as a tick. Only drawn once there is something to average. */}
      {row.mean_ratio !== null && row.points.length > 1 ? (
        <g>
          <line
            x1={x(row.mean_ratio)}
            x2={x(row.mean_ratio)}
            y1={1}
            y2={HEIGHT - 1}
            stroke="var(--color-border-strong)"
            strokeWidth={2}
          />
          <title>{`Mean ${formatPercent(row.mean_ratio)} across ${row.points.length} sittings`}</title>
        </g>
      ) : null}

      {row.points.map((point) => (
        <g key={point.attempt_id}>
          <title>
            {`${point.exam_title}${
              point.attempt_no > 1 ? ` (resit #${point.attempt_no})` : ""
            } — ${formatPercent(point.ratio)} on ${formatDate(point.submitted_at)}`}
          </title>
          {point.attempt_no > 1 ? (
            <rect
              x={x(point.ratio) - 3.5}
              y={MID - 3.5}
              width={7}
              height={7}
              fill="var(--color-surface)"
              stroke="var(--color-ink)"
              strokeWidth={1.5}
            />
          ) : (
            <circle
              cx={x(point.ratio)}
              cy={MID}
              r={3.5}
              fill="var(--color-surface)"
              stroke="var(--color-ink)"
              strokeWidth={1.5}
            />
          )}
        </g>
      ))}
    </svg>
  );
}

export function AnalyticsCohortScores({ rows }: { rows: CohortStudentRow[] }) {
  const withScores = rows.filter((r) => r.points.length > 0);
  const withoutScores = rows.filter((r) => r.points.length === 0);

  if (rows.length === 0) {
    return (
      <p className="px-4 py-6 text-xs text-ink-faint">
        Nobody has sat an exam yet. This fills in with one row per student as
        attempts are submitted — add people on the roster first.
      </p>
    );
  }

  return (
    <div className="px-4 pb-4">
      <ul className="divide-y divide-border">
        {withScores.map((row) => (
          <li key={row.student_id} className="flex items-center gap-3 py-1.5">
            <Link
              to={`/admin/students/${row.student_id}`}
              className="w-44 shrink-0 truncate text-xs font-medium text-ink hover:underline"
            >
              {row.student_name}
            </Link>
            <div className="min-w-0 flex-1">
              <StudentStrip row={row} />
            </div>
            <span className="tabular w-32 shrink-0 text-right text-2xs text-ink-muted">
              {row.mean_ratio === null ? "—" : formatPercent(row.mean_ratio)}
              <span className="text-ink-faint">
                {" "}
                · n={row.points.length}
                {row.retakes > 0 ? `, ${row.retakes} resit` : ""}
              </span>
            </span>
          </li>
        ))}
      </ul>

      {/* The axis, aligned to the strips above by matching the row layout. */}
      <div className="flex items-center gap-3 pt-1">
        <span className="w-44 shrink-0" />
        <div className="min-w-0 flex-1">
          <svg viewBox={`0 0 ${WIDTH} 12`} className="w-full" aria-hidden="true">
            {TICKS.map((tick) => (
              <text
                key={tick}
                x={(tick / 100) * WIDTH}
                y={9}
                textAnchor={tick === 0 ? "start" : tick === 100 ? "end" : "middle"}
                fontSize={8}
                fill="var(--color-ink-faint)"
              >
                {tick}
              </text>
            ))}
          </svg>
        </div>
        <span className="w-32 shrink-0" />
      </div>

      <div className="flex items-center gap-3">
        <span className="w-44 shrink-0" />
        <p className="min-w-0 flex-1 text-2xs text-ink-faint">
          Score, % of the paper (0–100). Circle = first sitting, square = resit,
          thick tick = mean. Right-hand figure is the mean and the number of
          graded sittings it averages.
        </p>
        <span className="w-32 shrink-0" />
      </div>

      {withoutScores.length > 0 ? (
        <p className="mt-2 text-2xs text-ink-faint">
          Not plotted, nothing graded yet:{" "}
          {withoutScores.map((r, i) => (
            <span key={r.student_id}>
              {i > 0 ? ", " : ""}
              <Link
                to={`/admin/students/${r.student_id}`}
                className="text-ink-muted hover:underline"
              >
                {r.student_name}
              </Link>
            </span>
          ))}
          .
        </p>
      ) : null}
    </div>
  );
}
